import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import Button from '../../shared/TailwindComponents/Button';
import Input from '../../shared/TailwindComponents/Input';
import Textarea from '../../shared/TailwindComponents/Textarea';
import DraftTypeSelector from './DraftTypeSelector';
import { PiPlusLight } from 'react-icons/pi';
import { createDraftTemplate, deleteDraftTemplate, fetchDraftTemplates, fetchCaseDraftTypes, updateDraftTemplate } from '../../../store/actions/Drafts/DraftTypesActions';
import Badge from '../../shared/TailwindComponents/Badge';

interface DraftTemplate {
  templateId: number;
  name: string;
  description: string;
  draftTypeIds: number[];
}

export interface DraftType {
  Description: string;
  DraftID: number;
  Name: string;
  ParentDraftID: number | null;
  Status: string;
  IsActive: boolean;
}

interface DraftTemplateManagerProps {
  caseId: string; 
  onClose: () => void;
  onSelectTemplate: (draftTypeIds: number[]) => void;
}

interface TemplateFormProps {
  template: DraftTemplate;
  setTemplate: (template: DraftTemplate) => void;
  onSave: () => void;
  onCancel: () => void;
  isEditing: boolean;
  allDraftTypes: DraftType[];
}

const TemplateForm: React.FC<TemplateFormProps> = ({ template, setTemplate, onSave, onCancel, isEditing, allDraftTypes }) => {
  const { t } = useTranslation();

  return (
    <div className="">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">{isEditing ? t('Edit Template') : t('Create New Template')}</h3>
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel} variant="outline" size="small">
            {t('Cancel')}
          </Button>
          <Button onClick={onSave} variant="primary" size="small" disabled={!template.name}>
            {isEditing ? t('Update') : t('Save')}
          </Button>
        </div>
      </div>
      <Input
        type="text"
        placeholder={t('Template Name')}
        value={template.name}
        onChange={(e) => setTemplate({ ...template, name: e.target.value })}
        className="mb-3"
      />
      <Textarea
        placeholder={t('Description (optional)')}
        value={template.description}
        onChange={(e) => setTemplate({ ...template, description: e.target.value })}
        rows={2}
        className="mb-3"
        id={''}
        label={''}
      />
      <DraftTypeSelector
        allDraftTypes={allDraftTypes}
        selectedDraftTypeIds={template.draftTypeIds}
        onChange={(ids: number[]) => setTemplate({ ...template, draftTypeIds: ids })}
      />
    </div>
  );
};

const DraftTemplateManager: React.FC<DraftTemplateManagerProps> = ({ caseId, onClose, onSelectTemplate }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [templates, setTemplates] = useState<DraftTemplate[]>([]);
  const [mode, setMode] = useState<'list' | 'create' | 'edit'>('list');
  const [newTemplate, setNewTemplate] = useState({ templateId: 0, name: '', description: '', draftTypeIds: [] as number[] });
  const [editingTemplate, setEditingTemplate] = useState<DraftTemplate | null>(null);
  const [allDraftTypes, setAllDraftTypes] = useState<DraftType[]>([]);

  useEffect(() => {
    dispatch(fetchDraftTemplates()).then((data) => setTemplates(data));
    dispatch(fetchCaseDraftTypes(caseId)).then((data) => setAllDraftTypes(data));
  }, [dispatch, caseId]);

  const handleCreateTemplate = () => {
    dispatch(createDraftTemplate(newTemplate)).then(() => {
      setNewTemplate({ templateId: 0, name: '', description: '', draftTypeIds: [] });
      setMode('list');
      dispatch(fetchDraftTemplates()).then((data) => setTemplates(data));
    });
  };

  const handleUpdateTemplate = () => {
    if (editingTemplate) {
      dispatch(updateDraftTemplate(editingTemplate.templateId, editingTemplate)).then(() => {
        setEditingTemplate(null);
        setMode('list');
        dispatch(fetchDraftTemplates()).then((data) => setTemplates(data));
      });
    }
  };

  const handleDeleteTemplate = (templateId: number) => {
    dispatch(deleteDraftTemplate(templateId)).then(() => {
      dispatch(fetchDraftTemplates()).then((data) => setTemplates(data));
    });
  };

  const draftTypeMap = useMemo(() => {
    return allDraftTypes.reduce((acc, dt) => {
      acc[dt.DraftID] = dt.Name;
      return acc;
    }, {} as { [key: number]: string });
  }, [allDraftTypes]);

  if (mode === 'create') {
    return (
      <TemplateForm
        template={newTemplate}
        setTemplate={setNewTemplate}
        onSave={handleCreateTemplate}
        onCancel={() => setMode('list')}
        isEditing={false}
        allDraftTypes={allDraftTypes}
      />
    );
  }

  if (mode === 'edit' && editingTemplate) {
    return (
      <TemplateForm
        template={editingTemplate}
        setTemplate={setEditingTemplate}
        onSave={handleUpdateTemplate}
        onCancel={() => setMode('list')}
        isEditing={true}
        allDraftTypes={allDraftTypes}
      />
    );
  }

  return (
    <div className="p-4">
      <div className="grid grid-cols-1 gap-4">
        {templates.map((template) => (
          <div
            key={template.templateId}
            className="p-4 bg-info-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-700"
          >
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{template.name}</h3>
              <div className="flex gap-2">
                <Button onClick={() => onSelectTemplate(template.draftTypeIds)} variant="primary" size="small" className="bg-info-600 hover:bg-info-700">
                  {t('Apply')}
                </Button>
                <Button onClick={() => { setEditingTemplate(template); setMode('edit'); }} variant="outline" size="small" className="text-gray-600 dark:text-gray-300">
                  {t('Edit')}
                </Button>
                <Button onClick={() => handleDeleteTemplate(template.templateId)} variant="destructive" size="small" className="text-danger-500 hover:text-danger-600">
                  {t('Delete')}
                </Button>
              </div>
            </div>
            <div className="flex justify-between items-start">
              <p className="text-sm text-gray-600 dark:text-gray-400">{template.description || t('No description')}</p>
              <div className="text-sm text-gray-600 dark:text-gray-400 text-end">
                {template.draftTypeIds.length > 0 ? (
                  <>
                    {template.draftTypeIds.slice(0, 3).map(id => draftTypeMap[id]).join(', ')}
                    {template.draftTypeIds.length > 3 && ` (+${template.draftTypeIds.length - 3} more)`}
                  </>
                ) : (
                  'No draft types'
                )}
              </div>
            </div>
            <div className="mt-2 flex justify-between items-center">
              <Badge color="gray">
                {template.draftTypeIds.length} {t('draft types')}
              </Badge>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 p-4 flex-col gap-4 flex items-center justify-center rounded border-1 border border-gray-200 dark:border-gray-700 cursor-pointer" onClick={() => setMode('create')}>
        <PiPlusLight className='h-10 w-10 text-secondary-900 hover:opacity-60 transition' />
        <span className='text-sm text-gray-700'>{t('Create a new template')}</span>
      </div>
    </div>
  );
};

export default DraftTemplateManager;